import { X, CheckCircle, AlertCircle, Mail, User, Shield } from 'lucide-react'

interface ModalProps {
  isOpen: boolean
  onClose: () => void
  title: string
  type: 'success' | 'error' | 'info'
  children: React.ReactNode
}

export function Modal({ isOpen, onClose, title, type, children }: ModalProps) {
  if (!isOpen) return null

  const iconColor = {
    success: '#10b981',
    error: '#dc2626',
    info: '#3b82f6'
  }

  const bgColor = {
    success: '#d1fae5',
    error: '#fee2e2',
    info: '#dbeafe'
  }

  return (
    <div style={{
      position: 'fixed',
      inset: 0,
      background: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000,
      animation: 'fadeIn 0.2s ease-out'
    }}>
      <div style={{
        background: 'white',
        borderRadius: '16px',
        padding: '2rem',
        maxWidth: '500px',
        width: '90%',
        maxHeight: '90vh',
        overflowY: 'auto',
        animation: 'slideIn 0.3s ease-out'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1.5rem' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
            <div style={{
              width: '48px',
              height: '48px',
              borderRadius: '12px',
              background: bgColor[type],
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              {type === 'success' && <CheckCircle size={24} color={iconColor[type]} />}
              {type === 'error' && <AlertCircle size={24} color={iconColor[type]} />}
              {type === 'info' && <Mail size={24} color={iconColor[type]} />}
            </div>
            <h2 style={{ fontSize: '1.25rem', fontWeight: 700, color: 'var(--text)', margin: 0 }}>{title}</h2>
          </div>
          <button
            onClick={onClose}
            style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '0.5rem' }}
          >
            <X size={24} style={{ color: 'var(--text-secondary)' }} />
          </button>
        </div>
        {children}
      </div>
    </div>
  )
}

interface TeamMemberSuccessModalProps {
  isOpen: boolean
  onClose: () => void
  memberData: {
    email: string
    fullName: string
    tempPassword: string
    role: string
    businessName: string
  }
  emailSent: boolean
}

export function TeamMemberSuccessModal({ isOpen, onClose, memberData, emailSent }: TeamMemberSuccessModalProps) {
  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Team Member Added Successfully" type="success">
      <div style={{ display: 'grid', gap: '1.5rem' }}>
        <div style={{
          background: '#f0fdf4',
          border: '1px solid #bbf7d0',
          borderRadius: '12px',
          padding: '1.5rem'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '1rem' }}>
            <User size={20} color='#166534' />
            <span style={{ fontWeight: 600, color: '#166534' }}>Team Member Details</span>
          </div>
          <div style={{ display: 'grid', gap: '0.5rem' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <span style={{ color: '#6b7280' }}>Full Name:</span>
              <span style={{ fontWeight: 600, color: '#374151' }}>{memberData.fullName}</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <span style={{ color: '#6b7280' }}>Email:</span>
              <span style={{ fontWeight: 600, color: '#374151' }}>{memberData.email}</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <span style={{ color: '#6b7280' }}>Role:</span>
              <span style={{ fontWeight: 600, color: '#374151', textTransform: 'capitalize' }}>{memberData.role}</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <span style={{ color: '#6b7280' }}>Business:</span>
              <span style={{ fontWeight: 600, color: '#374151' }}>{memberData.businessName}</span>
            </div>
          </div>
        </div>

        <div style={{
          background: '#eff6ff',
          border: '1px solid #bfdbfe',
          borderRadius: '12px',
          padding: '1.5rem'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '1rem' }}>
            <Shield size={20} color='#1d4ed8' />
            <span style={{ fontWeight: 600, color: '#1d4ed8' }}>Login Credentials</span>
          </div>
          <div style={{ display: 'grid', gap: '0.5rem' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <span style={{ color: '#6b7280' }}>Email:</span>
              <span style={{ fontWeight: 600, color: '#374151', fontFamily: 'monospace' }}>{memberData.email}</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <span style={{ color: '#6b7280' }}>Password:</span>
              <span style={{ fontWeight: 600, color: '#374151', fontFamily: 'monospace' }}>{memberData.tempPassword}</span>
            </div>
          </div>
          <p style={{ fontSize: '0.875rem', color: '#6b7280', marginTop: '1rem', margin: 0 }}>
            <strong>Important:</strong> Please share these credentials with the team member. The password is strong (12+ characters with uppercase, lowercase, numbers, and special characters). They should change their password after first login.
          </p>
        </div>

        {emailSent ? (
          <div style={{
            background: '#d1fae5',
            border: '1px solid #bbf7d0',
            borderRadius: '8px',
            padding: '1rem',
            display: 'flex',
            alignItems: 'center',
            gap: '0.75rem'
          }}>
            <CheckCircle size={20} color='#059669' />
            <span style={{ color: '#059669', fontSize: '0.875rem', fontWeight: 600 }}>
              Invitation email sent successfully to {memberData.email}
            </span>
          </div>
        ) : (
          <div style={{
            background: '#fef3c7',
            border: '1px solid #fde68a',
            borderRadius: '8px',
            padding: '1rem',
            display: 'flex',
            alignItems: 'center',
            gap: '0.75rem'
          }}>
            <AlertCircle size={20} color='#d97706' />
            <span style={{ color: '#d97706', fontSize: '0.875rem', fontWeight: 600 }}>
              Email could not be sent. Please share the credentials manually.
            </span>
          </div>
        )}

        <button
          onClick={onClose}
          style={{
            width: '100%',
            padding: '0.875rem 1.5rem',
            background: 'linear-gradient(135deg, #dc2626 0%, #b91c1c 100%)',
            border: 'none',
            borderRadius: '8px',
            color: 'white',
            fontSize: '1rem',
            fontWeight: 600,
            cursor: 'pointer',
            transition: 'all 0.2s',
            boxShadow: '0 2px 4px rgba(220, 38, 38, 0.2)'
          }}
          onMouseOver={(e) => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 4px 6px rgba(220, 38, 38, 0.3)' }}
          onMouseOut={(e) => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = '0 2px 4px rgba(220, 38, 38, 0.2)' }}
        >
          OK
        </button>
      </div>
    </Modal>
  )
}

interface ErrorModalProps {
  isOpen: boolean
  onClose: () => void
  title: string
  message: string
}

export function ErrorModal({ isOpen, onClose, title, message }: ErrorModalProps) {
  return (
    <Modal isOpen={isOpen} onClose={onClose} title={title} type="error">
      <div style={{ display: 'grid', gap: '1rem' }}>
        <p style={{ color: '#374151', fontSize: '1rem', lineHeight: '1.5' }}>{message}</p>
        <button
          onClick={onClose}
          style={{
            width: '100%',
            padding: '0.875rem 1.5rem',
            background: 'linear-gradient(135deg, #dc2626 0%, #b91c1c 100%)',
            border: 'none',
            borderRadius: '8px',
            color: 'white',
            fontSize: '1rem',
            fontWeight: 600,
            cursor: 'pointer',
            transition: 'all 0.2s',
            boxShadow: '0 2px 4px rgba(220, 38, 38, 0.2)'
          }}
          onMouseOver={(e) => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 4px 6px rgba(220, 38, 38, 0.3)' }}
          onMouseOut={(e) => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = '0 2px 4px rgba(220, 38, 38, 0.2)' }}
        >
          OK
        </button>
      </div>
    </Modal>
  )
}

interface ConfirmModalProps {
  isOpen: boolean
  onClose: () => void
  onConfirm: () => void
  title: string
  message: string
  confirmText?: string
  cancelText?: string
}

export function ConfirmModal({ isOpen, onClose, onConfirm, title, message, confirmText = 'Confirm', cancelText = 'Cancel' }: ConfirmModalProps) {
  return (
    <Modal isOpen={isOpen} onClose={onClose} title={title} type="error">
      <div style={{ display: 'grid', gap: '1.5rem' }}>
        <p style={{ color: '#374151', fontSize: '1rem', lineHeight: '1.5' }}>{message}</p>
        <div style={{ display: 'flex', gap: '0.75rem' }}>
          <button
            onClick={onClose}
            style={{
              flex: 1,
              padding: '0.875rem 1.5rem',
              background: 'white',
              border: '2px solid var(--border)',
              borderRadius: '8px',
              color: 'var(--text)',
              fontSize: '1rem',
              fontWeight: 600,
              cursor: 'pointer',
              transition: 'all 0.2s'
            }}
            onMouseOver={(e) => { e.currentTarget.style.background = '#f9fafb' }}
            onMouseOut={(e) => { e.currentTarget.style.background = 'white' }}
          >
            {cancelText}
          </button>
          <button
            onClick={() => {
              onConfirm()
              onClose()
            }}
            style={{
              flex: 1,
              padding: '0.875rem 1.5rem',
              background: 'linear-gradient(135deg, #dc2626 0%, #b91c1c 100%)',
              border: 'none',
              borderRadius: '8px',
              color: 'white',
              fontSize: '1rem',
              fontWeight: 600,
              cursor: 'pointer',
              transition: 'all 0.2s',
              boxShadow: '0 2px 4px rgba(220, 38, 38, 0.2)'
            }}
            onMouseOver={(e) => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 4px 6px rgba(220, 38, 38, 0.3)' }}
            onMouseOut={(e) => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = '0 2px 4px rgba(220, 38, 38, 0.2)' }}
          >
            {confirmText}
          </button>
        </div>
      </div>
    </Modal>
  )
}
